import { create } from 'zustand';

interface AuthClient {
  me: () => Promise<unknown>;
  login: (email: string, password: string, rememberMe: boolean) => Promise<unknown>;
  logout: () => Promise<unknown>;
  changePassword: (current: string, next: string) => Promise<unknown>;
  changeEmail: (password: string, email: string) => Promise<unknown>;
}
export interface AuthState {
  isAuthenticated: boolean; isCheckingAuth: boolean; isLoading: boolean; error: string | null;
  checkAuth: () => Promise<void>;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (current: string, next: string) => Promise<void>;
  changeEmail: (password: string, email: string) => Promise<void>;
  invalidateSession: () => void;
}
export function authErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = error.response;
    if (typeof response === 'object' && response !== null && 'data' in response) {
      const data = response.data;
      if (typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string') return data.message.slice(0, 300);
    }
  }
  return fallback; // Never expose/log an Axios config containing passwords.
}
export function createAuthStore(client: AuthClient) {
  let generation = 0;
  return create<AuthState>((set, get) => {
    const action = async (work: () => Promise<unknown>, fallback: string, authenticated: boolean) => {
      if (get().isLoading) throw new Error('Authentication request already in progress');
      const revision = ++generation;
      set({ isLoading: true, isCheckingAuth: false, error: null });
      try {
        await work();
        if (revision !== generation) throw new Error('Authentication request superseded');
        set({ isAuthenticated: authenticated, error: null });
      } catch (error) {
        const message = authErrorMessage(error, fallback);
        if (revision === generation) set({ error: message });
        throw new Error(message); // Do not swallow failures or propagate credential-bearing errors.
      } finally {
        if (revision === generation) set({ isLoading: false });
      }
    };
    return {
      isAuthenticated: false, isCheckingAuth: true, isLoading: false, error: null,
      invalidateSession: () => { generation++; set({ isAuthenticated: false, isCheckingAuth: false, isLoading: false, error: null }); },
      checkAuth: async () => {
        if (get().isLoading) return;
        const revision = ++generation;
        set({ isCheckingAuth: true });
        try { await client.me(); if (revision === generation) set({ isAuthenticated: true }); }
        catch { if (revision === generation) set({ isAuthenticated: false }); }
        finally { if (revision === generation) set({ isCheckingAuth: false }); }
      },
      login: async (email, password, rememberMe = false) => {
        return action(() => { set({ isAuthenticated: false }); return client.login(email, password, rememberMe); }, 'Login failed', true);
      },
      // A failed logout does NOT prove that the server cleared its HttpOnly cookie.
      logout: () => action(() => client.logout(), 'Logout failed; please try again', false),
      changePassword: (current, next) => action(() => client.changePassword(current, next), 'Password change failed', false),
      changeEmail: (password, email) => action(() => client.changeEmail(password, email), 'Email change failed', false),
    };
  });
}
